import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { lazy, Suspense, useEffect, useRef } from "react";
import { SEOProvider } from "./components/seo/SEOHead";
import AllPropertiesPage from "./pages/AllPropertiesPage"; // Import the new page
import AboutUsPage from "./pages/AboutUsPage"; // Import the About Us page
import FutureProjectsPage from "./components/RealisticaFullAnimations";
import NotFound from "@/pages/not-found";
import Header from "./components/shared/Header";
import Footer from "./components/shared/Footer";
import WishlistNotification from "./components/ui/WishlistNotification";
import ShareModal from "./components/ui/ShareModal";
import ScrollToTopButton from "./components/ui/ScrollToTopButton";
import { AuthProvider } from "./hooks/use-auth";
import { ProtectedRoute } from "./lib/protected-route";
import { ProtectedCRMRoute } from "./components/crm/ProtectedCRMRoute";
import { CRMLayout } from "./components/crm/CRMLayout";
import ErrorBoundary from "./components/ErrorBoundary";

// Lazy load pages for better performance
const HomePage = lazy(() => import("./pages/HomePage"));
const PropertyDetailsPage = lazy(() => import("./pages/PropertyDetailsPage"));
const ProjectDetailsPage = lazy(() => import("./pages/ProjectDetailsPage"));
// PropertiesPage is no longer lazy loaded here as AllPropertiesPage is used for /immobilien
const AdminPage = lazy(() => import("./pages/AdminPage"));
const WishlistPage = lazy(() => import("./pages/WishlistPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ImpressumPage = lazy(() => import("./pages/ImpressumPage"));
const DatenschutzPage = lazy(() => import("./pages/DatenschutzPage"));
const AGBPage = lazy(() => import("./pages/AGBPage"));
const AuthPage = lazy(() => import("./pages/auth-page"));
// Blog pages
const BlogPage = lazy(() => import("./pages/BlogPage"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
// Project category pages
const KaufenPage = lazy(() => import("./pages/KaufenPage"));
const VerkaufenPage = lazy(() => import("./pages/VerkaufenPage")); // Using original VerkaufenPage (fullPage.js)
const ZukuenftigeProjektePage = lazy(() => import("./pages/ZukuenftigeProjektePage"));
const VergangeneProjektePage = lazy(() => import("./pages/VergangeneProjektePage"));
// YADE Signature luxury page
const YadeSignaturePage = lazy(() => import("./pages/YadeSignaturePage"));
// Email verification and download pages
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const DownloadPage = lazy(() => import("./pages/DownloadPage"));
const PrivacyRequestPage = lazy(() => import("./pages/PrivacyRequestPage"));
// CRM pages
const CRMLoginPage = lazy(() => import("./pages/crm/LoginPage"));
const CRMRegisterPage = lazy(() => import("./pages/crm/RegisterPage"));
const CRMDashboard = lazy(() => import("./pages/crm/DashboardPage"));
const CRMLeads = lazy(() => import("./pages/crm/LeadsPage"));
const CRMPipeline = lazy(() => import("./pages/crm/PipelinePage"));
const CRMCommunications = lazy(() => import("./pages/crm/CommunicationsPage"));
const CRMCalendar = lazy(() => import("./pages/crm/CalendarPage"));
const CRMTasks = lazy(() => import("./pages/crm/TasksPage"));
const CRMDocuments = lazy(() => import("./pages/crm/DocumentsPage"));
const CRMSettings = lazy(() => import("./pages/crm/SettingsPage"));
// Newsletter pages
const CampaignListPage = lazy(() => import("./pages/crm/newsletter/CampaignListPage"));
const CampaignEditorPage = lazy(() => import("./pages/crm/newsletter/CampaignEditorPage"));
const CampaignAnalyticsPage = lazy(() => import("./pages/crm/newsletter/CampaignAnalyticsPage"));
const SubscriberManagementPage = lazy(() => import("./pages/crm/newsletter/SubscriberManagementPage"));

function Router() {
  const [location] = useLocation();

  // Placeholder data for FutureProjectsPage component
  const placeholderProps = {
    area: "1500",
    location: "Berlin",
    price: 1200000,
    beds: 3,
    bath: 2,
    parking: "1",
    terrace: "100",
    projectStatus: "Completed",
    isNeubauprojekt: true,
    projectName: "Future Towers",
    projectType: "Apartment",
    heroImage: "/path/to/placeholder-hero-image.jpg", // Replace with a valid placeholder image path
    energyStatus: 90,
    marketMinPrice: 1000000,
    marketMaxPrice: 1500000,
    finishesImages: ["/path/to/finishes-image-1.jpg", "/path/to/finishes-image-2.jpg"], // Replace with valid placeholder image paths
    marketCompareSvg: "/path/to/market-compare.svg", // Replace with a valid placeholder SVG path
    marketCompareColorSvg: "/path/to/market-compare-color.svg", // Replace with a valid placeholder SVG path
  };

  return (
    <ErrorBoundary key={location}>
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Lädt...</div>}>
        <Switch>
          <Route path="/" component={HomePage} />
          <Route path="/immobilien" component={AllPropertiesPage} /> {/* Use the new page */}
          <Route path="/immobilie/:id" component={PropertyDetailsPage} />
          <Route path="/projekte/:slug" component={ProjectDetailsPage} />
          <Route path="/kaufen" component={KaufenPage} />
          <Route path="/verkaufen">
            <VerkaufenPageWrapper />
          </Route>
          <Route path="/zukuenftige-projekte" component={ZukuenftigeProjektePage} />
          <Route path="/vergangene-projekte" component={VergangeneProjektePage} />
          <Route path="/ueber-uns" component={AboutUsPage} />
          <Route path="/yade-signature" component={YadeSignaturePage} />
          <Route path="/blog" component={BlogPage} />
          <Route path="/blog/:slug" component={ArticlePage} />
          <Route path="/auth" component={AuthPage} />
          <ProtectedRoute path="/admin/:section?" component={AdminPage} />
          <ProtectedRoute path="/wunschliste" component={WishlistPage} />
          <Route path="/kontakt" component={ContactPage} />
          <Route path="/verify-email" component={VerifyEmailPage} />
          <Route path="/download" component={DownloadPage} />
          <Route path="/privacy-request" component={PrivacyRequestPage} />
          <Route path="/impressum" component={ImpressumPage} />
          <Route path="/datenschutz" component={DatenschutzPage} />
          <Route path="/agb" component={AGBPage} />
          {/* Route for futureprojects */}
          <Route path="/futureprojects">
            <FutureProjectsPage {...placeholderProps} />
          </Route>
          {/* CRM Routes */}
          <Route path="/crm/login" component={CRMLoginPage} />
          <Route path="/crm/register" component={CRMRegisterPage} />
          <Route path="/crm/dashboard">
            <CRMPageWrapper>
              <CRMDashboard />
            </CRMPageWrapper>
          </Route>
          <Route path="/crm/leads">
            <CRMPageWrapper>
              <CRMLeads />
            </CRMPageWrapper>
          </Route>
          <Route path="/crm/pipeline">
            <CRMPageWrapper>
              <CRMPipeline />
            </CRMPageWrapper>
          </Route>
          <Route path="/crm/communications">
            <CRMPageWrapper>
              <CRMCommunications />
            </CRMPageWrapper>
          </Route>
          <Route path="/crm/calendar">
            <CRMPageWrapper>
              <CRMCalendar />
            </CRMPageWrapper>
          </Route>
          <Route path="/crm/tasks">
            <CRMPageWrapper>
              <CRMTasks />
            </CRMPageWrapper>
          </Route>
          <Route path="/crm/documents">
            <CRMPageWrapper>
              <CRMDocuments />
            </CRMPageWrapper>
          </Route>
          <Route path="/crm/settings">
            <CRMPageWrapper>
              <CRMSettings />
            </CRMPageWrapper>
          </Route>
          {/* Newsletter Routes */}
          <Route path="/crm/newsletter/campaigns">
            <CRMPageWrapper>
              <CampaignListPage />
            </CRMPageWrapper>
          </Route>
          <Route path="/crm/newsletter/campaigns/new">
            <CRMPageWrapper>
              <CampaignEditorPage />
            </CRMPageWrapper>
          </Route>
          <Route path="/crm/newsletter/campaigns/:id/edit">
            <CRMPageWrapper>
              <CampaignEditorPage />
            </CRMPageWrapper>
          </Route>
          <Route path="/crm/newsletter/campaigns/:id/analytics">
            <CRMPageWrapper>
              <CampaignAnalyticsPage />
            </CRMPageWrapper>
          </Route>
          <Route path="/crm/newsletter/subscribers">
            <CRMPageWrapper>
              <SubscriberManagementPage />
            </CRMPageWrapper>
          </Route>
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  );
}

// Custom Wrapper für VerkaufenPage mit GARANTIERTER Cleanup-Strategie
function VerkaufenPageWrapper() {
  const [location] = useLocation();
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // CRITICAL: Clear any existing cleanup timeouts
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
    }

    // Cleanup GARANTIERT BEVOR React unmountet
    return () => {
      // 1. SOFORT: fullPage.js API cleanup (synchron)
      const fpApi = (window as any).fullpage_api;
      if (fpApi) {
        try {
          // Destroy mit 'all' parameter entfernt ALLE DOM-Manipulationen
          fpApi.destroy('all');
          // Setze API-Referenz zurück
          (window as any).fullpage_api = null;
        } catch (e) {
          console.error('[VerkaufenPage] fullPage API cleanup error:', e);
        }
      }

      // 2. DOM-Cleanup mit micro-delay (asynchron aber garantiert vor React unmount)
      cleanupTimeoutRef.current = setTimeout(() => {
        try {
          // Remove all fullPage DOM modifications
          const fpElements = document.querySelectorAll(
            '.fp-section, .fp-slide, .fp-slidesContainer, .fp-tableCell, .fp-scroller, [id^="fp-nav"]'
          );
          fpElements.forEach(el => {
            try {
              // Remove parent wenn es ein fullPage wrapper ist
              if (el.parentElement && el.parentElement.id === 'fullpage') {
                el.parentElement.innerHTML = '';
              } else {
                el.remove();
              }
            } catch (err) {
              console.warn('[VerkaufenPage] Element cleanup warning:', err);
            }
          });

          // 3. Body cleanup - entferne alle fullPage classes und styles
          document.body.classList.remove(
            'fp-viewing-0', 'fp-viewing-1', 'fp-viewing-2', 'fp-viewing-3',
            'fp-viewing-4', 'fp-viewing-5', 'fp-responsive', 'fp-auto-height',
            'fp-enabled', 'fp-destroyed'
          );
          document.body.style.removeProperty('overflow');
          document.body.style.removeProperty('height');

          // 4. HTML cleanup
          document.documentElement.classList.remove('fp-enabled');
          document.documentElement.style.removeProperty('overflow');
          document.documentElement.style.removeProperty('height');

        } catch (e) {
          console.error('[VerkaufenPage] DOM cleanup error:', e);
        }
      }, 0); // Micro-task delay: runs after current execution but before React unmount
    };
  }, [location]); // Re-run on location change to ensure fresh state

  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Lädt...</div>}>
      <VerkaufenPage />
    </Suspense>
  );
}

// CRM Page Wrapper - applies authentication and layout
function CRMPageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full">
      <ProtectedCRMRoute>
        <CRMLayout>
          <Suspense fallback={<div className="h-full flex items-center justify-center">Lädt...</div>}>
            {children}
          </Suspense>
        </CRMLayout>
      </ProtectedCRMRoute>
    </div>
  );
}

function App() {
  const [location] = useLocation();

  // Scroll to top on page navigation and manage Lenis
  useEffect(() => {
    // Immediate scroll to top - use Lenis if available
    if (window.lenis) {
      window.lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }

    const manageLenis = async () => {
      // Disable/enable Lenis based on page type
      if (location.startsWith('/crm')) {
        // Completely destroy Lenis on CRM pages to allow normal overflow scrolling
        if (window.lenis) {
          window.lenis.destroy();
          window.lenis = null;
        }

        // Remove Lenis classes that interfere with normal scrolling
        document.documentElement.classList.remove('lenis', 'lenis-smooth', 'lenis-scrolling', 'lenis-stopped');

        // Reset overflow and height for CRM pages
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.documentElement.style.height = '100%';
        document.body.style.height = '100%';
      } else {
        // Re-initialize Lenis on other pages if it doesn't exist
        if (!window.lenis) {
          const { initSmoothScroll } = await import('./lib/smoothScroll');
          window.lenis = initSmoothScroll();
        } else {
          window.lenis.start();
        }

        // Restore Lenis classes
        document.documentElement.classList.add('lenis', 'lenis-smooth');

        // Reset styles for non-CRM pages
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        document.documentElement.style.height = '';
        document.body.style.height = '';
      }
    };

    manageLenis();
  }, [location]);

  // Special pages that handle their own layout
  const isFullPageLayout = location === '/verkaufen' || location === '/yade-signature';
  const isCRMPage = location.startsWith('/crm');

  return (
    <SEOProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <div className={`flex flex-col ${!isFullPageLayout && !isCRMPage ? 'min-h-screen' : ''} ${isCRMPage ? 'h-screen overflow-hidden' : ''} font-opensans text-foreground bg-background`}>
            {!isCRMPage && <Header />}
            <main className={isCRMPage ? 'h-full' : 'flex-grow'}>
              <Router />
            </main>
            {!isFullPageLayout && !isCRMPage && <Footer variant={location === '/' ? 'homepage' : 'default'} />}
            <WishlistNotification />
            <ShareModal />
            <ScrollToTopButton />
          </div>
          <Toaster />
        </AuthProvider>
      </QueryClientProvider>
    </SEOProvider>
  );
}

export default App;
